/**
 * Products used on a treatment stage. A catalog stage flagged "releases
 * products" asks for them on the sheet; the backend takes them out of stock
 * the first time the stage starts. See docs/treatment-stage-release.md.
 */

export type StageProductLine = {
  productId: string;
  productName: string;
  brandName?: string;
  categoryName?: string;
  scale?: string;
  quantity: number;
  availableQuantity?: number;
};

const clean = (value: unknown) => String(value ?? "").trim();
const names = (list: unknown) => (Array.isArray(list) ? list.map(clean).filter(Boolean) : []);

/**
 * The flagged stage names for a sheet line, picked the same way as its stages:
 * the sub type's own stages, else the type's, else the treatment's.
 */
export function resolveReleaseStagesFromSelection(
  catalog: any[],
  treatmentId: string,
  typeId: string,
  subtypeId: string
): string[] {
  if (!catalog?.length || !treatmentId) return [];
  const main = catalog.find((item: any) => String(item._id) === String(treatmentId));
  if (!main) return [];
  const typeItem = typeId ? main.type?.find((t: any) => String(t._id) === String(typeId)) : null;

  if (subtypeId && typeItem) {
    const subtype = typeItem.subType?.find((s: any) => String(s._id) === String(subtypeId));
    if (names(subtype?.stages).length) return names(subtype?.releaseStages);
  }
  if (typeItem && names(typeItem.stages).length) return names(typeItem.releaseStages);
  return names(main.releaseStages);
}

/** The catalog item a sheet line takes its stages from (sub type → type → treatment). */
function stageSource(catalog: any[], treatmentId: string, typeId: string, subtypeId: string): any {
  if (!catalog?.length || !treatmentId) return null;
  const main = catalog.find((item: any) => String(item._id) === String(treatmentId));
  if (!main) return null;
  const typeItem = typeId ? main.type?.find((t: any) => String(t._id) === String(typeId)) : null;
  if (subtypeId && typeItem) {
    const subtype = typeItem.subType?.find((s: any) => String(s._id) === String(subtypeId));
    if (names(subtype?.stages).length) return subtype;
  }
  if (typeItem && names(typeItem.stages).length) return typeItem;
  return main;
}

export type StageCategoryLink = { categoryId: string; subCategoryIds: string[] };

/**
 * The inventory category a flagged stage is tied to in the catalog, or null
 * (then the sheet asks for a category as before). Same item as its stages.
 */
export function resolveStageCategoryFromSelection(
  catalog: any[],
  treatmentId: string,
  typeId: string,
  subtypeId: string,
  stage: string
): StageCategoryLink | null {
  const item = stageSource(catalog, treatmentId, typeId, subtypeId);
  const name = clean(stage);
  if (!item || !name || !names(item.releaseStages).includes(name)) return null;
  const hit = (Array.isArray(item.stageCategories) ? item.stageCategories : []).find(
    (l: any) => clean(l?.stage) === name
  );
  const categoryId = String(hit?.categoryId?._id ?? hit?.categoryId ?? "");
  if (!categoryId) return null;
  return {
    categoryId,
    subCategoryIds: (Array.isArray(hit.subCategoryIds) ? hit.subCategoryIds : [])
      .map((s: any) => String(s?._id ?? s ?? ""))
      .filter(Boolean),
  };
}

/**
 * A product's category name: the category it is stored under, else its sub
 * category's parent (populated by searchProductForStage). "" when neither.
 */
export function productCategoryName(product: any, categories: any[] = []): string {
  if (!product) return "";
  const direct = categories.find((c) => String(c?._id) === String(product.categoryId));
  return clean(direct?.categoryName || product.subCategoryId?.categoryId?.categoryName);
}

/** Add a line, or raise the quantity when that product is already listed. */
export function addStageProduct(lines: StageProductLine[], line: StageProductLine): StageProductLine[] {
  const at = lines.findIndex((l) => String(l.productId) === String(line.productId));
  if (at === -1) return [...lines, line];
  return lines.map((l, i) => (i === at ? { ...l, quantity: l.quantity + line.quantity } : l));
}

/**
 * Why a line can't be added, or "" when it can. `already` = qty of it already
 * listed, less what a released stage has already taken (so it can go below 0).
 */
export function stageProductError(line: Partial<StageProductLine>, already = 0): string {
  const quantity = Number(line.quantity);
  if (!line.productId) return "Choose a product.";
  if (!Number.isInteger(quantity) || quantity <= 0) return "Quantity must be a whole number above 0.";
  const stock = Number(line.availableQuantity);
  if (line.availableQuantity != null && quantity + already > stock) {
    return stock > 0 ? `Only ${stock} in stock.` : "Out of stock.";
  }
  return "";
}

/** The request body lines: product, name and quantity only. */
export const stageProductsPayload = (lines: StageProductLine[]) =>
  lines.map(({ productId, productName, quantity }) => ({ productId, productName, quantity }));

/** Lines stored on a stage, back into form lines. */
export const stageProductsFromItem = (item: any): StageProductLine[] =>
  (Array.isArray(item?.products) ? item.products : []).map((p: any) => ({
    productId: String(p?.productId?._id || p?.productId || ""),
    productName: p?.productName || p?.productId?.productName || "",
    brandName: p?.brandName || "",
    categoryName: p?.categoryName || "",
    scale: p?.scale || "",
    quantity: Number(p?.quantity) || 0,
  }));

/** "Cement × 1, Gloves × 2" */
export const stageProductsSummary = (lines: { productName?: string; quantity?: number }[]) =>
  lines.map((l) => `${l.productName || "Product"} × ${l.quantity}`).join(", ");

/** Brands found in a product list, A–Z, for the brand picker. */
export function brandsInProducts(products: any[]): { _id: string; brandName: string }[] {
  const seen = new Map<string, string>();
  for (const p of products || []) {
    const id = p?.brandId?._id;
    if (id && !seen.has(String(id))) seen.set(String(id), p.brandId.brandName || "Unnamed");
  }
  return Array.from(seen, ([_id, brandName]) => ({ _id, brandName })).sort((a, b) =>
    a.brandName.localeCompare(b.brandName)
  );
}
